import React, { useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import ChatFileSelector from 'components/DisasterGPT/ChatFileSelector';
import {
  useAttachedFiles,
  useAttachFiles,
  useDetachFile,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewChip } from 'components/DesignSystems/New/StylishNewChip';
import StylishViewFileDialog from 'components/DesignSystems/New/StylishViewFileDialog';
import { useSignedUrl } from 'components/FileManagement/hooks/useNotebookQueries';
import Loader from 'components/Loader/Loader';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { toast } from 'react-toastify';

interface ReportsFileAttachmentsProps {
  sitrepId: string;
  workspaceId: string | null; // incident_id from sitrep
  /**
   * When true, renders the attached files list without the ability to add or
   * remove attachments. Used on surfaces where attachments are only meant to be
   * viewed (e.g. Review, Current, Historical).
   */
  readOnly?: boolean;
}

const ReportsFileAttachments: React.FC<ReportsFileAttachmentsProps> = ({
  sitrepId,
  workspaceId,
  readOnly = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [fileViewUrl, setFileViewUrl] = useState<string | null>(null);
  const [currentFileName, setCurrentFileName] = useState<string>('');

  // Query hooks
  const { data: attachedFiles = [], isLoading: loadingAttached } =
    useAttachedFiles(sitrepId);

  // Mutation hooks
  const attachMutation = useAttachFiles();
  const detachMutation = useDetachFile();
  const { mutateAsync: getSignedUrl } = useSignedUrl();

  const handleAddFiles = async () => {
    if (selectedFileIds.length === 0) return;

    // Filter out files that are already attached to avoid duplicates
    const alreadyAttachedIds = attachedFiles.map((f) => f.id);
    const newFileIds = selectedFileIds.filter(
      (id) => !alreadyAttachedIds.includes(id)
    );

    // Only attach if there are new files
    if (newFileIds.length === 0) {
      setIsExpanded(false);
      return;
    }

    await attachMutation.mutateAsync({
      sitrepId,
      fileIds: newFileIds,
    });

    // Reset selection and collapse
    setSelectedFileIds([]);
    setIsExpanded(false);
  };

  const handleDetachFile = async (fileId: string) => {
    await detachMutation.mutateAsync({
      sitrepId,
      fileId,
    });
  };

  const handleViewFile = async (fileUrl: string, fileName: string) => {
    try {
      const url = await getSignedUrl(fileUrl);
      setFileViewUrl(url);
      setCurrentFileName(fileName);
      setShowFileDialog(true);
    } catch (error) {
      console.error('Error viewing file:', error);
      toast.error('Failed to view file');
    }
  };

  const isDisabled = !workspaceId;

  const addFilesButton = (
    <StylishNewButton
      className="button button--secondary button--sm"
      onClick={() => {
        // Pre-select already attached files
        setSelectedFileIds(attachedFiles.map((f) => f.id));
        setIsExpanded(true);
      }}
      disabled={isDisabled}
    >
      <SharedIcon iconName="add" size="sm" />
      <span className="ms-1">Add Files</span>
    </StylishNewButton>
  );

  return (
    <div className="reports-file-attachments">
      {/* Attached Files Display */}
      {workspaceId && loadingAttached ? (
        <Loader />
      ) : attachedFiles.length > 0 ? (
        <div className="mb-3">
          <div className="d-flex align-items-center mb-2 gap-2">
            <SharedIcon iconName="attach_file" size="sm" />
            <span className="fw-medium small">
              Attached Files ({attachedFiles.length})
            </span>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {attachedFiles.map((file) => (
              <StylishNewChip
                key={file.id}
                label={file.file_name}
                handleClick={() => handleViewFile(file.fileUrl, file.file_name)}
                handleDelete={
                  readOnly ? undefined : () => handleDetachFile(file.id)
                }
              />
            ))}
          </div>
        </div>
      ) : null}

      {/* Add Files Button/Selector */}
      {!readOnly &&
        (!isExpanded ? (
          isDisabled ? (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="add-files-tooltip">
                  Link this report to an incident to attach files
                </Tooltip>
              }
            >
              <span>{addFilesButton}</span>
            </OverlayTrigger>
          ) : (
            addFilesButton
          )
        ) : (
          <div className="mt-2">
            {/* Use exact same wrapper as AI Chat DataSources.js line 801 */}
            <div className="rounded border">
              <ChatFileSelector
                workspaceId={workspaceId!}
                selectedFileIds={selectedFileIds}
                onFileSelectionChange={setSelectedFileIds}
                selectionText="selected"
              />
            </div>

            {/* Action buttons below the selector */}
            <div className="d-flex justify-content-end mt-2 gap-2">
              <StylishNewButton
                className="button button--tertiary button--sm"
                onClick={() => {
                  setIsExpanded(false);
                  setSelectedFileIds([]);
                }}
              >
                Cancel
              </StylishNewButton>
              <StylishNewButton
                className="button button--primary button--sm"
                onClick={handleAddFiles}
                disabled={
                  selectedFileIds.length === 0 || attachMutation.isPending
                }
              >
                {attachMutation.isPending ? 'Attaching...' : 'Attach Files'}
              </StylishNewButton>
            </div>
          </div>
        ))}

      {/* File View Dialog */}
      {showFileDialog && fileViewUrl && (
        <StylishViewFileDialog
          show={showFileDialog}
          onClose={() => setShowFileDialog(false)}
          fileUrl={fileViewUrl}
          fileName={currentFileName}
        />
      )}
    </div>
  );
};

export default ReportsFileAttachments;
